package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.ConditionalContentAst
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ForContentAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.StyleRuleAst

/**
 * 编译期 style-class 样式层应用 Pass。
 *
 * 输入 [ScreenAst]，对元素树（从 root 递归，含组件展开后的所有元素）按 screen 顶层
 * `styles { ... }` 块中声明的规则做样式合并：
 *
 * - **命中判定**：规则命中元素 当且仅当
 *   `(rule.typeKind == null || rule.typeKind == el.kind) && rule.classes ⊆ el.styleClasses`。
 * - **specificity 排序**：specificity = `(typeKind!=null?1:0) + classes.size`；
 *   按 specificity 升序合并，并列按 styles 列表源顺序（越后越高）。
 * - **合并**：用 [LinkedHashMap] 按 specificity 升序逐条把规则 props 覆盖写入；
 *   最后把元素自身内联 props 覆盖写入（内联永远最高优先级）。
 * - 规则 props 的 value 是 ExprAst，原样并入，由下游正常校验 / 发射。
 *
 * 产出新 [ScreenAst]：root 替换为合并后的元素树、[ScreenAst.styles] 清空、
 * 每个元素的 [ElementAst.styleClasses] 清空（纯编译期产物，不进入 IR）。
 *
 * 应放在组件展开之后（这样组件展开出的元素也能被规则命中）、NativeClassResolverPass /
 * SemanticChecker 之前。
 */
class StyleClassApplyPass {

    /**
     * 入口：把 [styles] 规则应用到 [screen] 的元素树。
     *
     * @param styles 样式规则列表；默认取 [ScreenAst.styles]，调用方亦可显式传入。
     */
    fun apply(screen: ScreenAst, styles: List<StyleRuleAst> = screen.styles): ScreenAst {
        if (styles.isEmpty()) {
            // 无规则：仅清空 styleClasses 与 styles，保证不残留到 IR。
            return screen.copy(root = clearStyleClasses(screen.root), styles = emptyList())
        }
        return screen.copy(root = applyToElement(screen.root, styles), styles = emptyList())
    }

    /** 递归对单个元素应用规则，并对其子树（含 conditional / for 包装体）递归处理。 */
    private fun applyToElement(element: ElementAst, styles: List<StyleRuleAst>): ElementAst {
        val mergedProps = mergeProps(element, styles)
        return element.copy(
            props = mergedProps,
            children = element.children.map { applyToElement(it, styles) },
            conditionalContent = element.conditionalContent?.let { applyToConditional(it, styles) },
            forContent = element.forContent?.let { applyToFor(it, styles) },
            styleClasses = emptyList(),
        )
    }

    /**
     * 计算命中规则、按 specificity 升序合并 props，最后叠加元素自身内联 props。
     *
     * [LinkedHashMap] 保证：先写入的 key 维持插入顺序；后写入同名 key 覆盖值但不改顺序。
     */
    private fun mergeProps(
        element: ElementAst,
        styles: List<StyleRuleAst>,
    ): List<ElementAst.PropAssign> {
        // 收集命中规则，并保留其在 styles 列表中的源序号用于稳定排序。
        val matched = styles.withIndex().filter { (_, rule) -> matches(rule, element) }
        if (matched.isEmpty()) return element.props

        // 按 (specificity 升序, 源序号升序) 排序：specificity 高者后写入覆盖；并列按源顺序，越后越高。
        val sorted = matched.sortedWith(
            compareBy({ specificity(it.value) }, { it.index }),
        )

        val merged = LinkedHashMap<String, ElementAst.PropAssign>()
        for ((_, rule) in sorted) {
            for (prop in rule.props) {
                merged[prop.name] = prop
            }
        }
        // 元素自身内联 props 覆盖规则（内联永远最高优先级）。
        for (prop in element.props) {
            merged[prop.name] = prop
        }
        return merged.values.toList()
    }

    /** 命中判定：类型匹配（或规则无类型）且规则类名是元素 styleClasses 的子集。 */
    private fun matches(rule: StyleRuleAst, element: ElementAst): Boolean {
        if (rule.typeKind != null && rule.typeKind != element.kind) return false
        return element.styleClasses.containsAll(rule.classes)
    }

    /** specificity = (有类型 ? 1 : 0) + 类名个数。 */
    private fun specificity(rule: StyleRuleAst): Int =
        (if (rule.typeKind != null) 1 else 0) + rule.classes.size

    /** 对 conditional 包装体的各 branch.body 与 elseBranch 递归应用规则。 */
    private fun applyToConditional(
        content: ConditionalContentAst,
        styles: List<StyleRuleAst>,
    ): ConditionalContentAst = content.copy(
        branches = content.branches.map { it.copy(body = applyToElement(it.body, styles)) },
        elseBranch = content.elseBranch?.let { applyToElement(it, styles) },
    )

    /** 对 for 包装体的循环体递归应用规则。 */
    private fun applyToFor(
        content: ForContentAst,
        styles: List<StyleRuleAst>,
    ): ForContentAst = content.copy(body = applyToElement(content.body, styles))

    /**
     * 无规则时的退化处理：递归把整棵树的 styleClasses 清空，避免编译期产物残留到 IR。
     *
     * props 原样保留（无规则可合并），仅清 styleClasses 并递归子树 / 包装体。
     */
    private fun clearStyleClasses(element: ElementAst): ElementAst = element.copy(
        children = element.children.map { clearStyleClasses(it) },
        conditionalContent = element.conditionalContent?.let { c ->
            c.copy(
                branches = c.branches.map { it.copy(body = clearStyleClasses(it.body)) },
                elseBranch = c.elseBranch?.let { clearStyleClasses(it) },
            )
        },
        forContent = element.forContent?.let { it.copy(body = clearStyleClasses(it.body)) },
        styleClasses = emptyList(),
    )
}
